import React, { useEffect, useState, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useStyles from './styles';
import {
  drawChoropleth,
  drawBoundary,
  drawRegionLabel,
  drawMarkers,
  drawLegend,
  drawSummary
} from './drawing';

const TILE_OPACITY = 0.35;

export default props => {
  const classes = useStyles();
  const map = useMap(props);
  useChoropleth(map, props);
  useBoundary(map, props);
  useRegionLabel(map, props);
  useMarkers(map, props);
  useLegends(map, props);
  useSummaryControl(map, props);
  useRightClickEvent(map, props);

  return (
    <div>
      <div id="map" className={classes.root} />
    </div>
  );
};

const useMap = props => {
  const [map, setMap] = useState(null);

  const osmUrl =
    'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}' +
    (L.Browser.retina ? '@2x.png' : '.png');
  const osmAttrib =
    '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>';
  const tile_options = {
    attribution:
      '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>' +
      osmAttrib,
    opacity: TILE_OPACITY,
    detectRetina: true
  };
  const CENTER = [23.836, 90.28];
  const INIT_ZOOM = 7;

  useEffect(() => {
    const tileOsm = L.tileLayer(osmUrl, tile_options);
    const map_options = {
      center: CENTER,
      zoom: INIT_ZOOM,
      layers: [tileOsm],
      zoomControl: true,
      zoomSnap: 0,
      zoomDelta: 0.5
    };
    const map = L.map('map', map_options);
    //map.zoomControl.setPosition('bottomright');
    map.tile = tileOsm;
    setMap(map);
  }, []);
  return map;
};

const useChoropleth = (map, props) => {
  const layer = useRef(null);
  useEffect(() => {
    if (!map || !props.choropleth) return;
    layer.current = drawChoropleth(map, props.choropleth, props.regionAction);
    return () => {
      map.removeLayer(layer.current);
    };
  }, [props.choropleth, map]);
  return layer;
};

const useBoundary = (map, props) => {
  const layer = useRef(null);
  useEffect(() => {
    if (!map || !props.boundary) return;
    layer.current = drawBoundary(map, props.boundary);
    map.tile.setOpacity(1);
    return () => {
      map.tile.setOpacity(TILE_OPACITY);
      map.removeLayer(layer.current);
    };
  }, [props.boundary, map]);
  return layer;
};

const useRegionLabel = (map, props) => {
  const layer = useRef(null);
  useEffect(() => {
    if (!map || !props.regionLabel) return;
    layer.current = drawRegionLabel(map, props.regionLabel);
    return () => {
      map.removeLayer(layer.current);
    };
  }, [props.regionLabel, map]);
  return layer;
};

const useMarkers = (map, props) => {
  const layer = useRef(null);
  useEffect(() => {
    if (!map || !props.markers) return;
    layer.current = drawMarkers(map, props.markers);
    return () => {
      map.removeLayer(layer.current);
    };
  }, [props.markers, map]);
  return layer;
};

const useLegends = (map, props) => {
  const layer = useRef(null);
  useEffect(() => {
    if (!map || !props.legends) return;
    layer.current = drawLegend(map, props.legends);
    return () => {
      map.removeControl(layer.current);
    };
  }, [props.legends, map]);
  return layer;
};

const useSummaryControl = (map, props) => {
  const layer = useRef(null);
  useEffect(() => {
    if (!map || !props.summary) return;
    layer.current = drawSummary(map, props.summary);
    return () => {
      map.removeControl(layer.current);
    };
  }, [props.summary, map]);
  return layer;
};

const useRightClickEvent = (map, props) => {
  useEffect(() => {
    if (!map || !props.rightClickEvent) return;
    if (props.rightClickEvent) map.on('contextmenu', props.rightClickEvent);
    return () => {
      map.removeEventListener('contextmenu');
    };
  }, [props.rightClickEvent, map]);
};
